import { apiRequest } from './http';

// 네트워크 관리 API — utils.admon_network_metadata 한 장을 다룬다.
// 이 테이블이 정본이다(파생 아님). 그래서 행 전체를 다시 쓰지 않고 사람이 관리하는
// 값만 갱신한다.

// 세 번째 값은 null 이고 '자동 리프레시를 하지 않는다'는 뜻이다 — 미설정이 아니다.
export type RefreshPolicy = 'compare_max' | 'always_refresh';

// 정책은 주기마다 따로 정한다. 월간은 되는데 분기는 리포트 쿼터 때문에 안 되는 경우가
// 실제로 있다(fyber: 하루 50회, 하루치가 1회라 90일치를 하루에 발급할 수 없다).
export type RefreshCadence = 'monthly' | 'quarterly';

// 미디에이션 리포트의 네트워크 정보 한 건.
// mediation = 어느 플랫폼의 값인가(applovinMAX / AdmobPro / LevelPlay / APS / adjust / appsflyer).
// api_name = 리포팅 API 값(판정 매칭 키), dash_name = 대시보드 표시명.
// MAX 는 둘이 항상 다르고 LevelPlay·APS 는 api_name 이 없다.
// locale = 그 라벨이 어느 언어로 호출했을 때 오는 값인가. 같은 네트워크라도 국가에 따라
// 라벨이 달라져서 항목이 따로 존재한다('Mintegral (bidding)' / 'Mintegral(입찰)').
// custom_network 는 CUSTOM_NETWORK_SDK 처럼 공유되는 값을 가르는 하위 구분자인데,
// ad_network_pub.max 에 대응 수집 컬럼이 없어 아직 판정에는 쓰이지 않는다.
export interface MediationEntry {
  mediation: string;
  locale: string;
  api_name: string | null;
  dash_name: string | null;
  custom_network: string | null;
}

export interface NetworkMetaRow {
  sc_naming: string;
  // 기존 연계용 GAM 계정 코드. 새 저장에서는 전체 접두어에서 서버가 추출한다.
  gam_network_code: string[];
  gam_placement_prefixes?: string[];
  mediation_info: MediationEntry[];
  bi_network_name: string[];
  refresh_policy_monthly: RefreshPolicy | null;
  refresh_policy_quarterly: RefreshPolicy | null;
  threshold_pct: number | null;
  // 리프레시 판정 근거. refresh_policy_*·threshold_pct 와 한 묶음이다. 화면 이름은 '특이사항'.
  refresh_note: string | null;
  updated_at: string | null;
  // 마지막으로 고친 사람. 요청함을 거친 변경이면 승인자가 아니라 요청한 사람이다.
  // null 이면 Orbit 이 아직 손대지 않은 행이다(씨앗 그대로이거나 SQL 로 직접 고친 행).
  updated_by: string | null;
}

export interface NetworkMetaListResponse {
  networks: NetworkMetaRow[];
  refresh_policies: RefreshPolicy[];
  refresh_cadences: RefreshCadence[];
  // 실제로 쓰이는 미디에이션 목록. 상세 화면의 탭을 이걸로 만든다 — 목록을 프런트에
  // 박아 두면 새 미디에이션이 들어와도 탭이 안 생겨 값이 화면에서 사라진다.
  mediations: string[];
  generated_at: string;
}

// 행 소속 값만 담는다. 판정 4필드는 그룹 것이라 GroupPolicySaveBody 로만 바뀐다.
export interface NetworkMetaSaveBody {
  bi_network_name: string[];
  gam_placement_prefixes: string[];
  mediation_info: MediationEntry[];
  // 낙관적 동시성 토큰 — 상세를 읽은 시점의 updated_at.
  expected_updated_at: string | null;
  // 변경 사유. 요청함을 거치는 경우에만 쓰인다.
  reason?: string | null;
}

export interface NetworkMetaCreateBody {
  sc_naming: string;
  reason?: string | null;
  bi_network_name: string[];
  mediation_info: MediationEntry[];
  refresh_policy_monthly: RefreshPolicy | null;
  refresh_policy_quarterly: RefreshPolicy | null;
  threshold_pct: number | null;
  refresh_note: string | null;
}

export interface NetworkMetaWriteResult {
  applied: boolean;
  network: NetworkMetaRow | null;
  request_id: number | null;
}


// GET /api/network-meta/networks — 전 행(46행 규모). 정렬·검색은 클라이언트가 한다.
export function fetchNetworkMetaList(signal?: AbortSignal): Promise<NetworkMetaListResponse> {
  return apiRequest<NetworkMetaListResponse>('/network-meta/networks', { signal });
}

// GET /api/network-meta/networks/{sc_naming} — 상세 1행.
export function fetchNetworkMeta(scNaming: string, signal?: AbortSignal): Promise<NetworkMetaRow> {
  return apiRequest<NetworkMetaRow>(
    `/network-meta/networks/${encodeURIComponent(scNaming)}`,
    { signal },
  );
}

// PUT /api/network-meta/networks/{sc_naming} — 이름(mediation_info) + 판정 컬럼 저장.
// 409면 그 사이 다른 저장이 있었다는 뜻이므로 화면을 다시 불러야 한다.
export function saveNetworkMeta(
  scNaming: string,
  body: NetworkMetaSaveBody,
): Promise<NetworkMetaWriteResult> {
  return apiRequest<NetworkMetaWriteResult>(
    `/network-meta/networks/${encodeURIComponent(scNaming)}`,
    { method: 'PUT', body },
  );
}

// 그룹(판정 단위) 정책 저장 body. 낙관적 잠금이 그룹 토큰(행 수 + 최신 updated_at)이다.
export interface GroupPolicySaveBody {
  refresh_policy_monthly: RefreshPolicy | null;
  refresh_policy_quarterly: RefreshPolicy | null;
  threshold_pct: number | null;
  refresh_note: string | null;
  expected_row_count: number;
  expected_max_updated_at: string | null;
  reason: string | null;
}

// PUT /api/network-meta/groups/{group}/policy — 그룹 전 행에 판정 반영(요청함 경유).
export function saveGroupPolicy(
  group: string,
  body: GroupPolicySaveBody,
): Promise<NetworkMetaWriteResult> {
  return apiRequest<NetworkMetaWriteResult>(
    `/network-meta/groups/${encodeURIComponent(group)}/policy`,
    { method: 'PUT', body },
  );
}

// POST /api/network-meta/networks — 새 sc_naming 추가. 이미 있으면 409.
export function createNetworkMeta(
  body: NetworkMetaCreateBody,
): Promise<NetworkMetaWriteResult> {
  return apiRequest<NetworkMetaWriteResult>('/network-meta/networks', {
    method: 'POST',
    body,
  });
}

// ── 아이콘 ─────────────────────────────────────────────────────────
// 아이콘은 정본 표가 아니라 GCS 객체(NetworkIcons/{sc_naming}.png)다. 그래서 요청함을
// 거치지 않고 바로 반영된다. 백엔드가 프록시하므로 <img src> 에 그대로 쓴다. 없으면 404 —
// NetworkThumb 이 약자 대체를 그린다.

// 아이콘 캐시 버전 — 네트워크별로 마지막 업로드 시각을 든다. 페이지 state 에 두면 화면을
// 떠났다 돌아올 때 0 으로 돌아가 브라우저 캐시(max-age=300)의 옛 이미지가 보이고, 전역 값
// 하나면 업로드 1건에 모든 썸네일이 재요청된다. 모듈 맵 + sessionStorage 라 SPA 이동과
// 새로고침을 견디고, 바뀐 네트워크의 URL 만 달라진다.
const ICON_VERSION_KEY = 'orbit.networkIconVersions';

function readIconVersions(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(ICON_VERSION_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

const iconVersions: Record<string, number> = readIconVersions();

// 업로드 성공 직후 호출한다. 호출한 쪽이 재렌더를 일으켜야 새 URL 이 화면에 반영된다.
export function bumpNetworkIconVersion(scNaming: string): void {
  iconVersions[scNaming] = Date.now();
  try {
    sessionStorage.setItem(ICON_VERSION_KEY, JSON.stringify(iconVersions));
  } catch {
    // 저장 실패는 무해하다 — 이 탭 안에서는 모듈 맵이 살아 있다.
  }
}

// GET /api/network-meta/networks/{sc_naming}/icon. 업로드된 적이 있는 네트워크만 ?v= 가 붙는다.
export function networkIconUrl(scNaming: string): string {
  const base = `/api/network-meta/networks/${encodeURIComponent(scNaming)}/icon`;
  const version = iconVersions[scNaming];
  return version ? `${base}?v=${version}` : base;
}

export interface NetworkIconUploadResult {
  sc_naming: string;
  uploaded: string;
}

// POST /api/network-meta/networks/{sc_naming}/icon — PNG data URL 을 올린다(edit_request 이상).
// 정본 행이 아직 없는 이름(추가 요청 대기 중)도 허용된다.
export function uploadNetworkIcon(
  scNaming: string,
  imageBase64: string,
): Promise<NetworkIconUploadResult> {
  return apiRequest<NetworkIconUploadResult>(
    `/network-meta/networks/${encodeURIComponent(scNaming)}/icon`,
    { method: 'POST', body: { image_base64: imageBase64 } },
  );
}

// ── 리셀러 매핑 수동 동기화(admin) ─────────────────────────────────
// GAM 코드가 바뀐 승인은 자동으로 재생성된다. 이 호출은 관리자가 같은 재생성을 직접 시작한다.

export interface ResellerRefreshEvent {
  id: number;
  event_type: string;
  note: string | null;
  actor_email: string | null;
  request_id: number | null;
  trigger: string | null;
  created_at: string;
}

export interface ResellerRefreshStatus {
  enabled: boolean;
  dry_run: boolean;
  running: boolean;
  events: ResellerRefreshEvent[];
}

export function fetchResellerRefreshStatus(signal?: AbortSignal): Promise<ResellerRefreshStatus> {
  return apiRequest<ResellerRefreshStatus>('/network-meta/reseller-refresh', { signal });
}

// 202 로 바로 돌아온다 — 결과는 상태 조회의 최근 이벤트로 본다. 꺼져 있으면 503, 실행 중이면 409.
// requestId 를 주면 그 승인 요청의 재시도라 이벤트가 요청 상세에 남는다.
export function startResellerRefresh(requestId?: number): Promise<{ started: boolean; dry_run: boolean }> {
  return apiRequest<{ started: boolean; dry_run: boolean }>('/network-meta/reseller-refresh', {
    method: 'POST',
    body: requestId === undefined ? {} : { request_id: requestId },
  });
}
